import React, { useCallback, useEffect, useRef, useState } from 'react';
import icons from '../../assets/icons';
import Table from '../../components/table';
import createColumns from './columns';
import utils from '../../utils';
import './styles.scss';
import UploadFileModal from './uploadFileModal';
import CustomButton from '../../components/customButton';
import API from '../../services/api';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedPortal } from '../../redux/portal/actions';
import CustomConfirm from '../../components/customConfirm';
import { Link } from 'react-router-dom';

const PointOfSaleContainer = ({ lastUpdated }) => {
  const [data, setData] = useState([]);
  const [items, setItems] = useState([]);
  const [itemsCount, setItemsCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const portal = useSelector(getSelectedPortal);
  const dispatch = useDispatch();
  const isPointOfSaleActive = portal?.activeFeatures?.includes('POS') || false;

  const fetchIdRef = useRef(0);

  useEffect(() => {
    setData([]);
    setItems([]);
    setItemsCount(0);
    setPageCount(0);

    if (portal?.id && isPointOfSaleActive) {
      fetchPOS();
    } else {
      setLoading(false);
      setIsModalOpen(false);
      setIsDeleteModalOpen(false);
      setSelectedFile(null);
    }
  }, [portal?.id, isPointOfSaleActive]);

  useEffect(() => {
    if (lastUpdated && isPointOfSaleActive) {
      fetchPOS();
    }
  }, [lastUpdated]);

  const fetchPOS = async () => {
    if (portal?.id && isPointOfSaleActive) {
      setItems([]);
      setLoading(true);
      API.getPosReports(portal.id)
        .then(res => {
          setData(res.pointOfSaleReports);
        })
        .catch(() => {
          dispatch(utils.generateAlert('Failed to fetch POS reports', 'danger'));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const fetchData = useCallback(
    ({ pageIndex, pageSize, sortBy }) => {
      if (data.length === 0) return;
      // Give this fetch an ID
      const fetchId = ++fetchIdRef.current;

      // Generate sort config object
      const sortByQuery = sortBy[0];
      const sortByConfig = utils.generateSortByConfig(sortByQuery, 'displayName', tableColumns);

      // Perform data sort
      const items = utils.sort(data, sortByConfig);

      // Only update the data if this is the latest fetch
      if (fetchId === fetchIdRef.current) {
        const startRow = pageSize * pageIndex;
        const endRow = startRow + pageSize;

        // Retrieve fetched data
        setItems(items.slice(startRow, endRow));

        // Compute total number of items
        setItemsCount(items.length);

        // Compute total page count
        setPageCount(Math.ceil(items.length / pageSize));
      }
    },
    [data]
  );

  const handleDeleteModal = async file => {
    if (!file) return;
    setIsDeleteModalOpen(true);
    setSelectedFile(file);
  };

  const handleDelete = (id, brandPortalId) => {
    setLoading(true);
    API.deletePosReport(brandPortalId, id)
      .then(() => {
        dispatch(utils.generateAlert('File deleted successfully', 'success'));
        fetchPOS();
      })
      .catch(() => {
        dispatch(utils.generateAlert('Failed to delete file', 'danger'));
      })
      .finally(() => {
        setLoading(false);
        fetchPOS();
      });
  };

  const tableColumns = createColumns(handleDeleteModal);

  return (
    <div className="files-container">
      <div className="toolbar">
        <div className="title">
          <img src={icons.reportOutlineDark} alt="Reports" />
          <h2>Point of Sale Files History</h2>
        </div>
        {isPointOfSaleActive && (
          <CustomButton
            title={'Upload a new file'}
            handleClick={() => setIsModalOpen(true)}
            classes={'btn btn-slim btn-secondary'}
          />
        )}
      </div>
      <div className="files-content">
        {portal?.id && !isPointOfSaleActive ? (
          <div className="inactive-pos-feature">
            <h3>Point of Sale is inactive for this portal.</h3>
            <p>Activate the Point of Sale feature in the portal settings to upload and manage files.</p>
            <Link to="/portals">Go to portal settings</Link>
          </div>
        ) : data.length === 0 && !loading ? (
          <div className="empty-pos-table">
            <h3>No Point of Sale files uploaded yet.</h3>
            <p>Upload a file to start building the history for this portal.</p>
          </div>
        ) : (
          <Table
            dataLabel={'files'}
            columns={tableColumns}
            data={items}
            fetchData={fetchData}
            loading={loading}
            pageCount={pageCount}
            itemsCount={itemsCount}
            hasInteractiveRows
            // isItemDetailsClosed={!displayReportDetails}
            onItemSelected={() => {}}
            // actionButtons={report => actionButtons(report, loadReports, displayDeleteDialog, displayAlertMessage)}
          />
        )}
      </div>
      {isPointOfSaleActive && (
        <>
          <UploadFileModal
            isOpen={isModalOpen}
            toggle={() => setIsModalOpen(!isModalOpen)}
            portal={portal}
            confirmCallback={() => {
              fetchPOS();
            }}
          />
          <CustomConfirm
            isOpen={isDeleteModalOpen}
            toggle={() => setIsDeleteModalOpen(false)}
            messageType={'deleteReport'}
            messageContext={selectedFile?.id}
            cancelCb={() => {
              setSelectedFile(null);
              setIsDeleteModalOpen(false);
            }}
            confirmCb={() => {
              if (selectedFile) {
                handleDelete(selectedFile.id, portal.id);
              }
              setSelectedFile(null);
              setIsDeleteModalOpen(false);
            }}
          />
        </>
      )}
    </div>
  );
};

export default PointOfSaleContainer;
